import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { dataApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const OrganizationForm = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadOrganizationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const orgId = user?.organisation_id || user?.organisation?.$id;
      if (!orgId) {
        throw new Error('No organisation ID found');
      }
      
      const [orgData, customersData] = await Promise.all([
        dataApi.organisationGet(orgId),
        dataApi.organisationGetCustomers()
      ]);
      setOrganization(orgData);
      setCustomers(customersData || []);
    } catch (err) {
      setError('Failed to load organization data');
      console.error('Error loading organization data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      loadOrganizationData();
    }
  }, [isOpen, loadOrganizationData]);

  const handleSave = async () => {
    if (!organization) return;

    setSaving(true);
    setError(null);
    try {
      await dataApi.organisationUpdate(organization.$id, organization);
      onSave && onSave(organization);
      onClose();
    } catch (err) {
      setError('Failed to save organization data');
      console.error('Error saving organization:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setOrganization(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle loading state
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="dialog-large">
          <DialogHeader>
            <DialogTitle>Loading Organization...</DialogTitle>
            <DialogDescription>Loading organization information</DialogDescription>
          </DialogHeader>
          
          {/* Organization Details Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-24 w-full" />
              </div>
            </CardContent>
          </Card>

          {/* Settings Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-64" />
                  <Skeleton className="h-6 w-72" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customers Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="dialog-small">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>An error occurred while loading organization data</DialogDescription>
          </DialogHeader>
          <div className="text-center py-8 text-red-600">
            {error}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="dialog-large">
        <DialogHeader>
          <DialogTitle>Organization: {organization?.name || 'Organization Settings'}</DialogTitle>
          <DialogDescription>Edit organization information and settings</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Organization Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    className="input-borderless"
                    value={organization?.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    className="input-borderless"
                    value={organization?.contact_email || ''}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    className="input-borderless"
                    value={organization?.contact_phone || ''}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    className="input-borderless"
                    value={organization?.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessAddress">Business Address</Label>
                <Textarea
                  id="businessAddress"
                  className="input-borderless"
                  value={organization?.business_address || ''}
                  onChange={(e) => handleInputChange('business_address', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessDescription">Business Description</Label>
                <Textarea
                  id="businessDescription"
                  className="input-borderless"
                  value={organization?.business_description || ''}
                  onChange={(e) => handleInputChange('business_description', e.target.value)}
                  rows={4}
                  placeholder="Describe your business and services..."
                />
              </div>
            </CardContent>
          </Card>

          {/* License and Tax Information */}
          <Card>
            <CardHeader>
              <CardTitle>License & Tax Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taxId">Tax ID / Business License</Label>
                  <Input
                    id="taxId"
                    className="input-borderless"
                    value={organization?.tax_id || ''}
                    onChange={(e) => handleInputChange('tax_id', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="insuranceLicense">Insurance License Number</Label>
                  <Input
                    id="insuranceLicense"
                    className="input-borderless"
                    value={organization?.insurance_license || ''}
                    onChange={(e) => handleInputChange('insurance_license', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Settings Section */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="autoApproveQuotes"
                  checked={organization?.auto_approve_quotes || false}
                  onCheckedChange={(checked) => handleInputChange('auto_approve_quotes', checked)}
                />
                <Label htmlFor="autoApproveQuotes">
                  Automatically approve quotes under $500
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requireCustomerApproval"
                  checked={organization?.require_customer_approval || false}
                  onCheckedChange={(checked) => handleInputChange('require_customer_approval', checked)}
                />
                <Label htmlFor="requireCustomerApproval">
                  Require customer approval before finalizing quotes
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Organization Customers Section */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Customers ({customers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {customers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No customers found for this organization.
                </div>
              ) : (
                <div className="space-y-2">
                  {customers.map((customer) => (
                    <div key={customer.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div>
                          <strong>{customer.first_name} {customer.last_name}</strong>
                        </div>
                        <div className="text-sm text-gray-600">
                          {customer.email}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div>{customer.phone || 'N/A'}</div>
                        <div>{formatDate(customer.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationForm;